import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ActionIcon, Switch } from "@mantine/core";
import {
	IconChevronDown,
	IconChevronRight,
	IconChevronLeft,
	IconEye,
	IconX,
} from "@tabler/icons-react";
import API from "../../utilities/LocalApiProxy";
import { contextItemIcon } from "./contextItemIcon";
import {
	toggleAiGroupSourcesByMessage,
	toggleAiCitationsSidebar,
} from "../../actions/browserLocalstorageActions";

const downloadEmail = (emailID) => {
	API.get("/signedUrlForEmail", { queryParams: { emailID } })
		.then((res) => {
			window.location.href = res.mysql.url;
		})
		.catch(console.error);
};

const citationLinkStyle = {
	display: "block",
	fontSize: 13,
	color: "var(--lxw-hyperlink)",
	textDecoration: "underline",
	wordBreak: "break-word",
	cursor: "pointer",
	marginTop: 4,
	marginBottom: 4,
};

const typeLabelStyle = {
	fontSize: 11,
	textTransform: "uppercase",
	letterSpacing: "0.04em",
	display: "block",
	marginTop: 6,
	marginBottom: 2,
};

const CitationLink = ({ citation, onPreview }) => {
	const { itemType, id, title } = citation;
	const icon = contextItemIcon(itemType, title);

	// these links that go to another page in the app should open in a new tab so the user doesn't lose their place in the chat
	const link =
		itemType === "Document" ? (
			<a
				href={`/docs/${id}`}
				target="_blank"
				rel="noopener noreferrer"
				style={citationLinkStyle}
			>
				{title}
			</a>
		) : itemType === "Email" ? (
			<span
				role="button"
				style={citationLinkStyle}
				onClick={() => downloadEmail(id)}
			>
				{title}
			</span>
		) : (
			<a
				href={`/notes/${id}`}
				target="_blank"
				rel="noopener noreferrer"
				style={citationLinkStyle}
			>
				{title}
			</a>
		);

	return (
		<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
			{icon}
			{onPreview && (
				<ActionIcon
					variant="subtle"
					size="sm"
					title="Preview"
					style={{ flexShrink: 0, marginLeft: -8 }}
					onClick={(e) => {
						e.stopPropagation();
						onPreview(citation);
					}}
				>
					<IconEye size={13} />
				</ActionIcon>
			)}
			{link}
		</div>
	);
};

const TYPE_ORDER = [
	{ key: "Document", label: "Documents" },
	{ key: "Email", label: "Email" },
	{ key: "Matter Note", label: "Matter Notes" },
];

// Documents/emails/notes outside any real matter live in "Firm Docs" — the
// backend uses matterID 0 (or, from the aggregate-search tool, occasionally
// -1) for these; neither is a real matter to link to, and both are folded
// into a single "Firm Docs" group here regardless of which sentinel arrives.
const FIRM_DOCS_BUCKET_KEY = "__firmDocs__";
const isFirmDocsMatterID = (matterID) => matterID != null && matterID <= 0;

// citations carry a matterID/matterName (self-referencing for a bare "Matter"
// citation) so full-scope sources can be organized by matter; a "Matter"
// citation only anchors its matter's group into existence (e.g. the AI looked
// into that matter but didn't cite a specific document/email/note from it) -
// it's never itself listed as an item within the group
const groupCitationsByMatter = (citations) => {
	const matters = new Map();
	const bucketFor = (matterID, matterName) => {
		const isFirmDocs = isFirmDocsMatterID(matterID);
		const key = isFirmDocs
			? FIRM_DOCS_BUCKET_KEY
			: (matterID ?? "__unknown__");
		if (!matters.has(key)) {
			matters.set(key, {
				matterID: isFirmDocs ? matterID : (matterID ?? null),
				matterName: isFirmDocs ? "Firm Docs" : matterName || "Other",
				isFirmDocs,
				items: [],
			});
		}
		return matters.get(key);
	};
	citations.forEach((c) => {
		if (c.itemType === "Matter") {
			bucketFor(c.matterID ?? c.id, c.matterName ?? c.title);
			return;
		}
		bucketFor(c.matterID, c.matterName).items.push(c);
	});
	return Array.from(matters.values()).sort((a, b) =>
		a.matterName.localeCompare(b.matterName)
	);
};

const TypeGroupedCitations = ({ items, onPreview, showCounts }) => {
	const byType = items.reduce((acc, c) => {
		(acc[c.itemType] = acc[c.itemType] || []).push(c);
		return acc;
	}, {});
	const orderedTypes = TYPE_ORDER.filter((t) => byType[t.key]);
	return (
		<>
			{orderedTypes.map(({ key, label }) => (
				<div key={key}>
					<span style={typeLabelStyle}>
						{showCounts
							? `${label} (${byType[key].length})`
							: label}
					</span>
					{byType[key].map((c, i) => (
						<CitationLink
							key={i}
							citation={c}
							onPreview={onPreview}
						/>
					))}
				</div>
			))}
		</>
	);
};

const MatterGroup = ({ matter, onPreview }) => {
	const [collapsed, setCollapsed] = useState(true);
	const count = matter.items.length;
	const hasItems = count > 0;
	return (
		<div style={{ padding: "6px 0" }}>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 4,
					cursor: hasItems ? "pointer" : "default",
					userSelect: "none",
				}}
				onClick={hasItems ? () => setCollapsed((c) => !c) : undefined}
			>
				{hasItems ? (
					collapsed ? (
						<IconChevronRight
							size={13}
							color="#555"
							style={{ flexShrink: 0 }}
						/>
					) : (
						<IconChevronDown
							size={13}
							color="#555"
							style={{ flexShrink: 0 }}
						/>
					)
				) : (
					<span style={{ width: 13, flexShrink: 0 }} />
				)}
				{contextItemIcon(
					"Matter",
					matter.matterName,
					matter.isFirmDocs
				)}
				{onPreview && matter.matterID && !matter.isFirmDocs ? (
					<ActionIcon
						variant="subtle"
						size="sm"
						title="Preview"
						style={{ flexShrink: 0 }}
						onClick={(e) => {
							e.stopPropagation();
							onPreview({
								itemType: "Matter",
								id: matter.matterID,
								title: matter.matterName,
							});
						}}
					>
						<IconEye size={13} />
					</ActionIcon>
				) : null}
				<span
					style={{
						fontSize: 12,
						fontFamily: "MainFont-Bold",
						color: "#444",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						flex: 1,
					}}
				>
					{matter.matterName}
				</span>
				{hasItems ? (
					<span style={sectionCountBadgeStyle}>{count}</span>
				) : null}
			</div>
			{hasItems && !collapsed ? (
				<div style={{ paddingLeft: 18, paddingTop: 2 }}>
					<TypeGroupedCitations
						items={matter.items}
						onPreview={onPreview}
					/>
				</div>
			) : null}
		</div>
	);
};

const MatterGroupedCitations = ({ citations, onPreview }) => (
	<>
		{groupCitationsByMatter(citations).map((matter) => (
			<MatterGroup
				key={matter.matterID ?? "unknown"}
				matter={matter}
				onPreview={onPreview}
			/>
		))}
	</>
);

const CitationGroup = ({
	index,
	group,
	onToggle,
	showFullPrompt,
	onPreview,
	fullScope,
}) => {
	const { citations, collapsed, userPrompt, timestamp } = group;
	const count = citations.length;

	const sourcesList = (
		<div style={{ paddingLeft: showFullPrompt ? 0 : 18, paddingTop: 2 }}>
			{fullScope ? (
				<MatterGroupedCitations
					citations={citations}
					onPreview={onPreview}
				/>
			) : (
				<TypeGroupedCitations items={citations} onPreview={onPreview} />
			)}
		</div>
	);

	if (showFullPrompt) {
		return (
			<div style={{ padding: "8px 0" }}>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 1,
						marginBottom: 4,
					}}
				>
					<span
						style={{
							fontSize: 12,
							fontFamily: "MainFont-Bold",
							color: "#444",
						}}
					>
						{userPrompt
							? `"${userPrompt}"`
							: `Response ${index + 1}`}
					</span>
					<span style={{ fontSize: 11, color: "#888" }}>
						{timestamp && `${timestamp} · `}({count}{" "}
						{count === 1 ? "source" : "sources"})
					</span>
				</div>
				{sourcesList}
			</div>
		);
	}

	return (
		<div style={{ padding: "8px 0" }}>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 4,
					cursor: "pointer",
					userSelect: "none",
				}}
				onClick={onToggle}
			>
				{collapsed ? (
					<IconChevronRight
						size={14}
						color="#555"
						style={{ flexShrink: 0 }}
					/>
				) : (
					<IconChevronDown
						size={14}
						color="#555"
						style={{ flexShrink: 0 }}
					/>
				)}
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 1,
						minWidth: 0,
					}}
				>
					<span
						style={{
							fontSize: 12,
							fontFamily: "MainFont-Bold",
							color: "#444",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{userPrompt || `Response ${index + 1}`}
					</span>
					<span style={{ fontSize: 11, color: "#888" }}>
						{timestamp && `${timestamp} · `}({count}{" "}
						{count === 1 ? "source" : "sources"})
					</span>
				</div>
			</div>
			{!collapsed && sourcesList}
		</div>
	);
};

const FlatCitationList = ({ citationGroups, onPreview, fullScope }) => {
	const seen = new Set();
	const deduped = [];
	for (const group of citationGroups) {
		for (const c of group.citations) {
			const key = `${c.itemType}:${c.id}`;
			if (!seen.has(key)) {
				seen.add(key);
				deduped.push(c);
			}
		}
	}
	return fullScope ? (
		<MatterGroupedCitations citations={deduped} onPreview={onPreview} />
	) : (
		<TypeGroupedCitations
			items={deduped}
			onPreview={onPreview}
			showCounts
		/>
	);
};

const sectionCountBadgeStyle = {
	fontSize: 12,
	color: "#888",
	background: "#eee",
	borderRadius: 10,
	padding: "0 6px",
};

const sectionLabelStyle = {
	fontSize: 13,
	textTransform: "uppercase",
	letterSpacing: "0.04em",
	fontFamily: "MainFont-Bold",
};

const SectionHeader = ({ label, count, collapsed, onToggle }) => (
	<div
		style={{
			display: "flex",
			alignItems: "center",
			gap: 6,
			marginBottom: collapsed ? 0 : 6,
			cursor: "pointer",
			userSelect: "none",
		}}
		onClick={onToggle}
	>
		{collapsed ? (
			<IconChevronRight
				size={14}
				color="#555"
				style={{ flexShrink: 0 }}
			/>
		) : (
			<IconChevronDown size={14} color="#555" style={{ flexShrink: 0 }} />
		)}
		<span style={sectionLabelStyle}>{label}</span>
		<span style={sectionCountBadgeStyle}>{count}</span>
	</div>
);

const AddedContextSection = ({
	addedContext,
	onRemove,
	onPreview,
	collapsed,
	onToggle,
	sectionRef,
}) => {
	if (!addedContext?.length) return null;
	return (
		<div
			ref={sectionRef}
			style={{
				flexShrink: 0,
				padding: "10px 12px",
				borderBottom: "1px solid var(--lw-medium-grey)",
			}}
		>
			<SectionHeader
				label="Added Context"
				count={addedContext.length}
				collapsed={collapsed}
				onToggle={onToggle}
			/>
			{!collapsed
				? addedContext.map((item) => (
						<div
							key={`${item.itemType}:${item.id}`}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 4,
								padding: "3px 0",
							}}
						>
							{contextItemIcon(item.itemType, item.title)}
							<div style={{ minWidth: 0, flex: 1 }}>
								<div
									style={{
										fontSize: 13,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{item.title}
								</div>
								<div style={{ fontSize: 11, color: "#888" }}>
									{item.itemType === "Matter"
										? item.clientName
										: item.matterName}
								</div>
							</div>
							{item.itemType === "Document" ||
							item.itemType === "Matter" ? (
								<ActionIcon
									variant="subtle"
									size="sm"
									title="Preview"
									onClick={() => onPreview(item)}
								>
									<IconEye size={13} />
								</ActionIcon>
							) : null}
							<ActionIcon
								variant="subtle"
								size="sm"
								title="Remove"
								onClick={() => onRemove(item)}
							>
								<IconX size={13} />
							</ActionIcon>
						</div>
					))
				: null}
		</div>
	);
};

const SourcesSection = ({
	citationGroups,
	onToggleGroup,
	filteredGroupIndex,
	onClearFilter,
	onPreviewCitation,
	groupByMessage,
	onToggleGroupByMessage,
	collapsed,
	onToggle,
	bottomRef,
	fullScope,
}) => {
	const hasAnyCitations = citationGroups.some((g) => g.citations.length > 0);
	if (!hasAnyCitations) return null;

	const isFiltered =
		filteredGroupIndex !== null && filteredGroupIndex !== undefined;
	const totalSourcesCount = new Set(
		citationGroups.flatMap((g) =>
			g.citations.map((c) => `${c.itemType}:${c.id}`)
		)
	).size;

	return (
		<div
			style={{
				flexGrow: collapsed ? 0 : 1,
				flexShrink: 0,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				padding: "10px 12px",
				overflow: "hidden",
			}}
		>
			<SectionHeader
				label="Sources"
				count={totalSourcesCount}
				collapsed={collapsed}
				onToggle={onToggle}
			/>
			{!collapsed ? (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						flexGrow: 1,
						minHeight: 0,
					}}
				>
					<Switch
						label="Group by message"
						size="xs"
						checked={groupByMessage ?? true}
						onChange={onToggleGroupByMessage}
						style={{ marginBottom: 8 }}
					/>
					{isFiltered ? (
						<div
							style={{
								background: "#f0f0f0",
								borderRadius: 4,
								padding: "6px 10px",
								fontSize: 12,
								color: "#666",
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 8,
								marginBottom: 8,
								flexShrink: 0,
							}}
						>
							<div>Showing sources for one response only.</div>
							<button
								onClick={onClearFilter}
								style={{
									fontSize: 11,
									color: "var(--lxw-hyperlink)",
									background: "none",
									border: "none",
									cursor: "pointer",
									padding: 0,
									textDecoration: "underline",
									flexShrink: 0,
								}}
							>
								Show all
							</button>
						</div>
					) : null}
					<div style={{ flexGrow: 1, overflowY: "auto" }}>
						{isFiltered ? (
							<CitationGroup
								index={filteredGroupIndex}
								group={citationGroups[filteredGroupIndex]}
								onToggle={() =>
									onToggleGroup(filteredGroupIndex)
								}
								showFullPrompt
								onPreview={onPreviewCitation}
								fullScope={fullScope}
							/>
						) : (groupByMessage ?? true) ? (
							citationGroups.map((group, i) =>
								group.citations.length > 0 ? (
									<CitationGroup
										key={i}
										index={i}
										group={group}
										onToggle={() => onToggleGroup(i)}
										onPreview={onPreviewCitation}
										fullScope={fullScope}
									/>
								) : null
							)
						) : (
							<FlatCitationList
								citationGroups={citationGroups}
								onPreview={onPreviewCitation}
								fullScope={fullScope}
							/>
						)}
						<div ref={bottomRef} />
					</div>
				</div>
			) : null}
		</div>
	);
};

const CitationsSidebar = ({
	citationGroups,
	onToggleGroup,
	filteredGroupIndex,
	onClearFilter,
	onPreviewCitation,
	addedContext = [],
	onRemoveContext,
	focusAddedContextTick = 0,
	fullScope,
}) => {
	const dispatch = useDispatch();
	const groupByMessage = useSelector(
		(state) => state.browserLocalstorage.aiGroupSourcesByMessage
	);
	const collapsed = useSelector(
		(state) => state.browserLocalstorage.aiCitationsSidebarCollapsed
	);
	const bottomRef = useRef(null);
	const addedContextRef = useRef(null);
	const [addedContextCollapsed, setAddedContextCollapsed] = useState(false);
	const [sourcesCollapsed, setSourcesCollapsed] = useState(false);

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [citationGroups.length]);

	useEffect(() => {
		if (!focusAddedContextTick) return;
		setAddedContextCollapsed(false);
		addedContextRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});
	}, [focusAddedContextTick]);

	const isFiltered =
		filteredGroupIndex !== null && filteredGroupIndex !== undefined;

	useEffect(() => {
		if (isFiltered) setSourcesCollapsed(false);
	}, [isFiltered]);

	const hasAnyCitations = citationGroups.some((g) => g.citations.length > 0);
	if (!hasAnyCitations && !addedContext.length) return null;

	if (collapsed) {
		return (
			<div
				style={{
					width: 32,
					flexShrink: 0,
					borderLeft: "1px solid var(--lw-medium-grey)",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					paddingTop: 12,
				}}
			>
				<ActionIcon
					variant="subtle"
					onClick={() => dispatch(toggleAiCitationsSidebar())}
					aria-label="Expand sidebar"
				>
					<IconChevronLeft size={16} />
				</ActionIcon>
				<span
					style={{
						fontSize: 11,
						color: "#555",
						textTransform: "uppercase",
						letterSpacing: "0.06em",
						writingMode: "vertical-rl",
						marginTop: 8,
					}}
				>
					Context & Sources
				</span>
			</div>
		);
	}

	return (
		<div
			style={{
				width: 450,
				flexShrink: 0,
				borderLeft: "1px solid var(--lw-medium-grey)",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			{/* Header */}
			<div
				style={{
					flexShrink: 0,
					padding: "12px 12px 8px",
					borderBottom: "1px solid var(--lw-medium-grey)",
					display: "flex",
					alignItems: "center",
					gap: 8,
				}}
			>
				<ActionIcon
					variant="subtle"
					onClick={() => dispatch(toggleAiCitationsSidebar())}
					aria-label="Collapse sidebar"
				>
					<IconChevronRight size={16} />
				</ActionIcon>
				<div
					style={{
						fontSize: 12,
						fontFamily: "MainFont-Bold",
						color: "#333",
						textTransform: "uppercase",
						letterSpacing: "0.06em",
					}}
				>
					Context & Sources
				</div>
			</div>

			<AddedContextSection
				addedContext={addedContext}
				onRemove={onRemoveContext}
				onPreview={onPreviewCitation}
				collapsed={addedContextCollapsed}
				onToggle={() => setAddedContextCollapsed((c) => !c)}
				sectionRef={addedContextRef}
			/>

			<SourcesSection
				citationGroups={citationGroups}
				onToggleGroup={onToggleGroup}
				filteredGroupIndex={filteredGroupIndex}
				onClearFilter={onClearFilter}
				onPreviewCitation={onPreviewCitation}
				groupByMessage={groupByMessage}
				onToggleGroupByMessage={() =>
					dispatch(toggleAiGroupSourcesByMessage())
				}
				collapsed={sourcesCollapsed}
				onToggle={() => setSourcesCollapsed((c) => !c)}
				bottomRef={bottomRef}
				fullScope={fullScope}
			/>
		</div>
	);
};

export default CitationsSidebar;
